/**
 * Merchant Pro RBAC (Phase R-2) — declarative role → capability map.
 *
 * Single source of truth for which roles can reach which `/owner/*`
 * routes, which sidebar entries each role sees, and where each role
 * lands after login.
 *
 * **R-2 Design discipline (§12 forward-looking directive)** — this
 * module is INTENTIONALLY pure data + pure helpers. No JSX, no React
 * imports, no auth-store imports. Future V2 may swap the static
 * NAV_ENTRIES / ROLE_LANDING maps for a backend-fetched
 * `RoleCapabilityMap` without rewriting any consumer (OwnerLayout,
 * RoleGate, App.js). Keep this file framework-agnostic.
 *
 * **Tier-1 invariant (4c-AC#13 → R2-AC#6)** — consumers MUST use the
 * `filterNavEntries(...)`, `allowedRolesForRoute(...)`,
 * `landingForRole(...)` helpers. NEVER write `user.role === 'cashier'`
 * inside the 7 shared operational components.
 */

import {
  LayoutDashboard, BarChart3, CalendarDays, ListChecks, Sparkles,
  MapPin, Users, Gift, Megaphone, Star, Settings, FileText, Cake,
  UserCheck, UserX, Crown, Grid3x3, ShieldCheck, UsersRound, Receipt,
  Store,
} from 'lucide-react';

// --------------------------------------------------------------------
// Roles
// --------------------------------------------------------------------

export const ROLES = Object.freeze({
  BRAND_OWNER:  'brand_owner',
  CASHIER:      'cashier',
  GAME_MASTER:  'game_master',
  SUPER_ADMIN:  'super_admin',
  CUSTOMER:     'customer',
});

/**
 * Operational roles allowed under `/owner/*`. super_admin is included
 * (OQ-RBAC-1 super-set) so direct URL access to Merchant Pro from a
 * super_admin session works, even though super_admin's *default*
 * landing is `/admin` (OQ-R2-A locked default).
 */
export const MERCHANT_PRO_ROLES = Object.freeze([
  ROLES.BRAND_OWNER,
  ROLES.CASHIER,
  ROLES.GAME_MASTER,
  ROLES.SUPER_ADMIN,
]);

/**
 * Pretty-print labels used by OwnerLayout header so cashier + GM see
 * "Cashier Console" / "Game Master Console" instead of the legacy
 * hardcoded "Brand Owner Dashboard".
 */
export const ROLE_LABELS = Object.freeze({
  [ROLES.BRAND_OWNER]:  'Brand Owner',
  [ROLES.CASHIER]:      'Cashier',
  [ROLES.GAME_MASTER]:  'Game Master',
  [ROLES.SUPER_ADMIN]:  'Super Admin',
});

export const CONSOLE_TITLES = Object.freeze({
  [ROLES.BRAND_OWNER]:  'Brand Owner Dashboard',
  [ROLES.CASHIER]:      'Cashier Console',
  [ROLES.GAME_MASTER]:  'Game Master Console',
  [ROLES.SUPER_ADMIN]:  'Super Admin · Brand View',
});

// --------------------------------------------------------------------
// Default post-login landing per role  (OQ-RBAC-4 default · OQ-R2-A locked)
// --------------------------------------------------------------------

// Phase MONETIZATION-ITEMS-1-3 (2026-06-19) — operator-locked default
// post-login destinations. Cashier on web → `/mobile-only` because
// the OneStamp loyalty cashier workflow is mobile-only (per
// `App.js` header docstring + `MobileOnlyPage.jsx` copy). Folded
// fix from the cashier-loyalty-capability audit (P1 routing bug).
const ROLE_LANDING = Object.freeze({
  [ROLES.BRAND_OWNER]:  '/owner',
  [ROLES.CASHIER]:      '/mobile-only',
  [ROLES.GAME_MASTER]:  '/owner/floor-plan',
  [ROLES.SUPER_ADMIN]:  '/admin',                // OQ-R2-A locked default
  [ROLES.CUSTOMER]:     '/customer',
});

/**
 * Resolve the default landing path for a role. Returns `/mobile-only`
 * as a safe fallback when the role isn't recognized.
 */
export function landingForRole(role) {
  return ROLE_LANDING[role] || '/mobile-only';
}

// --------------------------------------------------------------------
// NAV_ENTRIES — declarative sidebar manifest with per-entry requiredRoles
//                     + requiredCapability (Sub-phase RBAC-C1 · 2026-06-18)
// --------------------------------------------------------------------

const ALL_BRAND_CONFIG = Object.freeze([
  ROLES.BRAND_OWNER,
  ROLES.SUPER_ADMIN,
]);

const ALL_OPERATIONAL = Object.freeze([
  ROLES.BRAND_OWNER,
  ROLES.CASHIER,
  ROLES.GAME_MASTER,
  ROLES.SUPER_ADMIN,
]);

const NO_GAME_MASTER = Object.freeze([
  ROLES.BRAND_OWNER,
  ROLES.CASHIER,
  ROLES.SUPER_ADMIN,
]);

/**
 * The sidebar surface for Merchant Pro. Single source of truth — both
 * OwnerLayout (visible filtering) and App.js (route guarding) consume
 * the same `requiredRoles` declaration so visibility and access stay
 * in sync.
 *
 * Operator §B.1 narrowings honored: Manual Booking + Customer CRM
 * exclude `game_master`.
 *
 * **Sub-phase RBAC-C1 (2026-06-18)** — each entry now also carries
 * `requiredCapability` (single canonical capability ID per the §4
 * inventory in MERCHANT_PRO_RBAC_COMPLETION_RECON_AND_PLAN_2026-06-18).
 * `OwnerLayout` consumes the capability-first filter when the actor's
 * cap-set is loaded; falls back to `requiredRoles` while
 * `/api/owner/me/capabilities` is still in-flight (silent degradation
 * to R-2 behavior — no flash-of-empty-nav).
 *
 * Capability mapping is conservative — each nav entry picks the
 * single most representative capability for its page surface (e.g.
 * Reservations → `reservation.view_list`, Floor Plan →
 * `floor_plan.view`). Multi-capability pages (e.g. Branches →
 * `brand_config.manage_branches`) gate on the operator-facing
 * management cap because read-only browsing is not a separately-
 * gated state in V1.
 *
 * **Phase MONETIZATION-ITEMS-1-3 (2026-06-19)** — entries that belong
 * to the OneStamp Pro bundle (per `/app/memory/ONESTAMP_PLAN_MATRIX_2026-06-19.md`
 * §2.3) carry `requiredPlan: 'pro'`. `OwnerLayout` filters these out
 * for Basic brands (`Brand.pro_enabled === false`). Basic-tier
 * entries (Dashboard / Branches / Cashiers / Rewards / Birthday /
 * Brand Settings / Audit / Retention) carry no `requiredPlan` and
 * render unconditionally. Operator-locked Decision A: Birthday is
 * Basic; Promotions + Review Links are Pro (Clarifications 1 + 2).
 */
export const NAV_ENTRIES = Object.freeze([
  { to: '/owner',                     end: true,  label: 'Dashboard',         icon: LayoutDashboard, requiredRoles: ALL_OPERATIONAL, requiredCapability: 'dashboard.view' },
  { to: '/owner/analytics',                        label: 'Analytics',         icon: BarChart3,       requiredRoles: ALL_BRAND_CONFIG, requiredCapability: 'analytics.view', requiredPlan: 'pro' },
  { to: '/owner/reservations',                     label: 'Reservations',      icon: CalendarDays,    requiredRoles: ALL_OPERATIONAL, requiredCapability: 'reservation.view_list', requiredPlan: 'pro' },
  { to: '/owner/waitlist',                         label: 'Walk-in / Waitlist',icon: ListChecks,      requiredRoles: ALL_OPERATIONAL, requiredCapability: 'waitlist.view', requiredPlan: 'pro' },
  { to: '/owner/floor-plan',                       label: 'Floor Plan',        icon: Grid3x3,         requiredRoles: ALL_OPERATIONAL, requiredCapability: 'floor_plan.view', requiredPlan: 'pro', newPill: true },
  { to: '/owner/manual-booking',                   label: 'Manual Booking',    icon: Sparkles,        requiredRoles: NO_GAME_MASTER,  requiredCapability: 'manual_booking.commit', requiredPlan: 'pro', newPill: true },
  { to: '/owner/crm',                              label: 'Customer CRM',      icon: UserCheck,       requiredRoles: NO_GAME_MASTER,  requiredCapability: 'crm.view_list', requiredPlan: 'pro' },
  // Phase R4 (2026-06-29) — Receipt OCR Claim verification. No
  // `requiredCapability` field so the capability-filter path renders
  // this entry for every brand_owner / super_admin without needing a
  // dedicated cap seed. The sealed backend router enforces the real
  // RBAC on each endpoint.
  { to: '/owner/receipt-claims',                   label: 'Receipt Claims',    icon: Receipt,         requiredRoles: ALL_BRAND_CONFIG },
  // Phase R10.4c (2026-07-05) — Outlet Mapping master. Sidebar entry
  // for brand_owner / super_admin. Backend enforces the real RBAC.
  { to: '/owner/outlet-mapping',                   label: 'Outlet Mapping',    icon: Store,           requiredRoles: ALL_BRAND_CONFIG },
  { to: '/owner/branches',                         label: 'Branches',          icon: MapPin,          requiredRoles: ALL_BRAND_CONFIG, requiredCapability: 'brand_config.manage_branches' },
  { to: '/owner/cashiers',                         label: 'Cashiers',          icon: Users,           requiredRoles: ALL_BRAND_CONFIG, requiredCapability: 'brand_config.manage_cashiers' },
  { to: '/owner/retention',                        label: 'Retention',         icon: UserX,           requiredRoles: ALL_BRAND_CONFIG, requiredCapability: 'brand_config.view_retention' },
  { to: '/owner/rewards',                          label: 'Rewards',           icon: Gift,            requiredRoles: ALL_BRAND_CONFIG, requiredCapability: 'brand_config.manage_rewards' },
  { to: '/owner/membership',                       label: 'Membership',        icon: Crown,           requiredRoles: ALL_BRAND_CONFIG, requiredCapability: 'membership.manage_config', requiredPlan: 'pro' },
  { to: '/owner/promotions',                       label: 'Promotions',        icon: Megaphone,       requiredRoles: ALL_BRAND_CONFIG, requiredCapability: 'brand_config.manage_promotions', requiredPlan: 'pro' },
  { to: '/owner/birthday-promotions',              label: 'Birthday Rewards',  icon: Cake,            requiredRoles: ALL_BRAND_CONFIG, requiredCapability: 'brand_config.manage_promotions' },
  { to: '/owner/reviews',                          label: 'Review Links',      icon: Star,            requiredRoles: ALL_BRAND_CONFIG, requiredCapability: 'brand_config.manage_reviews', requiredPlan: 'pro' },
  { to: '/owner/settings',                         label: 'Brand Settings',    icon: Settings,        requiredRoles: ALL_BRAND_CONFIG, requiredCapability: 'brand_config.manage_settings' },
  { to: '/owner/audit',                            label: 'Audit Log',         icon: FileText,        requiredRoles: ALL_BRAND_CONFIG, requiredCapability: 'brand_config.view_audit' },
  // Sub-phase RBAC-C3 (2026-06-18) — staff management surfaces. Visible only
  // to actors with `staff.view` (effectively Brand Owner + super_admin per
  // C1 seed; Supervisor + Cashier + GM all lack `staff.*` capabilities).
  // Phase MONETIZATION-ITEMS-1-3 (2026-06-19) — Staff Mgmt is Pro.
  { to: '/owner/staff/roles',                      label: 'Staff Roles',       icon: ShieldCheck,     requiredRoles: ALL_BRAND_CONFIG, requiredCapability: 'staff.view', requiredPlan: 'pro' },
  { to: '/owner/staff/users',                      label: 'Staff Users',       icon: UsersRound,      requiredRoles: ALL_BRAND_CONFIG, requiredCapability: 'staff.view', requiredPlan: 'pro' },
]);

// --------------------------------------------------------------------
// ROUTE_ACCESS — per-route allowed-roles lookup (used by App.js gates)
// --------------------------------------------------------------------

/**
 * Path-prefix table. Order matters — first prefix match wins.
 * Routes universally accessible to all operational roles are NOT
 * listed; they inherit the outer `/owner` gate
 * (`MERCHANT_PRO_ROLES`).
 */
const ROUTE_ACCESS = Object.freeze([
  { prefix: '/owner/analytics',           allowedRoles: ALL_BRAND_CONFIG },
  { prefix: '/owner/manual-booking',      allowedRoles: NO_GAME_MASTER },
  { prefix: '/owner/crm',                 allowedRoles: NO_GAME_MASTER },
  { prefix: '/owner/branches',            allowedRoles: ALL_BRAND_CONFIG },
  { prefix: '/owner/cashiers',            allowedRoles: ALL_BRAND_CONFIG },
  { prefix: '/owner/retention',           allowedRoles: ALL_BRAND_CONFIG },
  { prefix: '/owner/rewards',             allowedRoles: ALL_BRAND_CONFIG },
  { prefix: '/owner/membership',          allowedRoles: ALL_BRAND_CONFIG },
  { prefix: '/owner/promotions',          allowedRoles: ALL_BRAND_CONFIG },
  { prefix: '/owner/birthday-promotions', allowedRoles: ALL_BRAND_CONFIG },
  { prefix: '/owner/reviews',             allowedRoles: ALL_BRAND_CONFIG },
  { prefix: '/owner/settings',            allowedRoles: ALL_BRAND_CONFIG },
  { prefix: '/owner/audit',               allowedRoles: ALL_BRAND_CONFIG },
]);

/**
 * Return the per-route allowed-roles list for a given pathname.
 * Falls back to `MERCHANT_PRO_ROLES` (the outer `/owner` gate) for
 * universally-shared routes like Dashboard, Reservations, Waitlist,
 * Floor Plan, Notifications.
 */
export function allowedRolesForRoute(pathname) {
  if (!pathname) return MERCHANT_PRO_ROLES;
  for (const rule of ROUTE_ACCESS) {
    if (pathname === rule.prefix || pathname.startsWith(`${rule.prefix}/`)) {
      return rule.allowedRoles;
    }
  }
  return MERCHANT_PRO_ROLES;
}

// --------------------------------------------------------------------
// filterNavEntries — used by OwnerLayout
// --------------------------------------------------------------------

/**
 * Filter a NAV array against a role, returning only entries the role
 * is allowed to see. Pure — no React, no side effects.
 *
 * Defensive on `role = null/undefined` (returns []) so the layout
 * renders an empty sidebar instead of crashing during boot hydration.
 *
 * **Phase MONETIZATION-ITEMS-1-3 (2026-06-19)** — second positional
 * arg `brandFlags = { proEnabled }` filters out `requiredPlan: 'pro'`
 * entries when `proEnabled === false`. When `brandFlags` is null /
 * undefined (e.g. brand-fetch still in-flight, or SA without
 * brand-context), the plan filter is bypassed (no flash-of-empty-nav).
 */
export function filterNavEntries(nav, role, brandFlags = null) {
  if (!role) return [];
  return nav.filter((entry) => {
    if (Array.isArray(entry.requiredRoles)
        && !entry.requiredRoles.includes(role)) return false;
    if (entry.requiredPlan === 'pro' && brandFlags
        && brandFlags.proEnabled === false) return false;
    return true;
  });
}

// --------------------------------------------------------------------
// filterNavEntriesByCapabilities — Sub-phase RBAC-C1 capability filter
// --------------------------------------------------------------------

/**
 * Filter NAV_ENTRIES against the actor's effective capability set.
 * Used by OwnerLayout after `/api/owner/me/capabilities` resolves.
 *
 * Each NAV entry's `requiredCapability` must be present in
 * `capSet` for the entry to render. Entries WITHOUT a
 * `requiredCapability` field render unconditionally (defensive —
 * matches the pre-C1 R-2 default-on behavior for forgotten entries
 * during a partial migration; in C1 every NAV row has a cap).
 *
 * **Backward-compat guarantee** — when `capSet` is empty (the cap
 * fetch hasn't resolved yet, or super_admin without seeded brand),
 * this returns `[]` and the OwnerLayout's role-based fallback path
 * keeps the sidebar visible. Without that fallback the nav would
 * flash empty on every page boot.
 */
export function filterNavEntriesByCapabilities(nav, capSet, brandFlags = null) {
  if (!capSet) return [];
  const set = capSet instanceof Set ? capSet : new Set(capSet || []);
  if (set.size === 0) return [];
  return nav.filter((entry) => {
    if (entry.requiredCapability && !set.has(entry.requiredCapability)) {
      return false;
    }
    // Phase MONETIZATION-ITEMS-1-3 (2026-06-19) — Pro-bundle entries
    // hidden when the brand is Basic. `brandFlags=null` (still
    // fetching) bypasses the plan filter to avoid flash-of-no-nav.
    if (entry.requiredPlan === 'pro'
        && brandFlags
        && brandFlags.proEnabled === false) {
      return false;
    }
    return true;
  });
}

// --------------------------------------------------------------------
// canAccessRoute — convenience predicate (consumers: tests, dev tools)
// --------------------------------------------------------------------

export function canAccessRoute(role, pathname) {
  if (!role) return false;
  return allowedRolesForRoute(pathname).includes(role);
}
